import { cloudPrinterApi } from '.';
import { FetchCtx } from '@shared/types/api';
import { VITE_APP_CLOUD_PRINTER_API_URL } from '@shared/config/envConfig';

/**
 * Рабочие места облачной печати. Контроллер: CloudPrinter.Gateway WorkstationsController
 * (`/v1/cloud-printer/workstations`), отдельный сервис cloud-printer-api.
 *
 * Для оператора «рабочее место» = принтер: десктоп-приложение регистрирует рабочее место
 * (`/register`), а оператор закрепляет его за собой (`/bind`). Связка строго 1↔1:
 * повторный bind на другое рабочее место снимает предыдущую привязку (перепривязка),
 * повторный bind на своё же — идемпотентный Success.
 *
 * Перенесено из fast-track-webui (`src/shared/api/cloudPrinter.ts`) без изменений логики.
 */

/** Рабочее место drop-off-точки (WorkstationHttpResponse). */
export interface CloudPrinterWorkstation {
  id: string;
  /** Имя, заданное при регистрации десктопом. Может отсутствовать. */
  name: string | null;
  dropOffPointId: string;
  /** Пользователь, за которым закреплено рабочее место. null — свободно. */
  boundUserId: string | null;
  createdAt: string;
}

/**
 * Статус привязки (`BindWorkstationStatus`, приходит строкой). ВАЖНО: ручка отвечает 200
 * во всех случаях, включая отказы, — ветвиться нужно по этому полю, а не по HTTP-коду.
 */
export type BindWorkstationStatus =
  | 'Success'
  | 'UserNotFound'
  | 'WorkstationNotFound'
  | 'WorkstationNotInDropOffPoint'
  | 'WorkstationAlreadyBound';

/** Ответ на создание задания печати. */
export interface CreatePrintJobResult {
  /** Идентификатор задания — для корреляции с результатом печати. */
  jobId: string;
}

/** Статус отвязки (`UnbindWorkstationStatus`, строкой). */
export type UnbindWorkstationStatus = 'Success' | 'UserNotFound';

/**
 * Гейтвей облачной печати задан для этого окружения. На стендах, где переменная не
 * подставлена, в baseURL остаётся литерал `REPLACE_...` — axios резолвит его
 * ОТНОСИТЕЛЬНО origin, запрос уходит на сам фронт и SPA-фолбэк отвечает 200 с index.html.
 * Такой «успех» ломал разбор ответа, поэтому в неподнятом окружении не ходим вовсе.
 */
export const IS_CLOUD_PRINTER_CONFIGURED = !VITE_APP_CLOUD_PRINTER_API_URL.startsWith('REPLACE_');

function requireCloudPrinterUrl(): void {
  if (!IS_CLOUD_PRINTER_CONFIGURED) {
    throw new Error('cloud-printer: API URL is not configured');
  }
}

const BASE = 'v1/cloud-printer/workstations';

/**
 * GET /v1/cloud-printer/workstations — рабочие места drop-off-точки текущего пользователя.
 * Список уже отфильтрован по точке из JWT, поэтому «чужих» точек в нём не будет —
 * но занятые другими операторами рабочие места приходят (с непустым boundUserId).
 */
export async function getCloudPrinterWorkstations(ctx?: FetchCtx): Promise<CloudPrinterWorkstation[]> {
  requireCloudPrinterUrl();
  const res = await cloudPrinterApi<CloudPrinterWorkstation[]>(BASE, { signal: ctx?.signal });
  // Не доверяем 200-му на слово: прокси или SPA-фолбэк могут вернуть HTML с тем же кодом,
  // и тогда дальше по коду ляжет `.find is not a function` вместе со всем сайдбаром.
  if (!Array.isArray(res.data)) throw new Error('cloud-printer: unexpected workstations payload');
  return res.data;
}

/** POST /v1/cloud-printer/workstations/bind — закрепить рабочее место за текущим пользователем. */
export async function bindCloudPrinterWorkstation(
  workstationId: string,
  ctx?: FetchCtx,
): Promise<BindWorkstationStatus> {
  requireCloudPrinterUrl();
  const res = await cloudPrinterApi.post<{ status: BindWorkstationStatus }>(
    `${BASE}/bind`,
    { workstationId },
    { signal: ctx?.signal },
  );
  return res.data.status;
}

/** POST /v1/cloud-printer/workstations/unbind — снять привязку текущего пользователя. */
export async function unbindCloudPrinterWorkstation(ctx?: FetchCtx): Promise<UnbindWorkstationStatus> {
  requireCloudPrinterUrl();
  const res = await cloudPrinterApi.post<{ status: UnbindWorkstationStatus }>(
    `${BASE}/unbind`,
    undefined,
    { signal: ctx?.signal },
  );
  return res.data.status;
}

/** Ключ react-query для списка рабочих мест — общий для чтения и инвалидации после bind. */
export const CLOUD_PRINTER_WORKSTATIONS_KEY = ['cloudPrinterWorkstations'] as const;

/** Отображаемое имя рабочего места: имя от десктопа, иначе короткий id. */
export function workstationDisplayName(ws: CloudPrinterWorkstation): string {
  const name = ws.name?.trim();
  return name && name.length > 0 ? name : ws.id.slice(0, 8);
}

/**
 * POST /v1/cloud-printer/print-jobs — отправить этикетку на привязанный принтер.
 * Контроллер: CloudPrinter.Gateway PrintJobsController. Тело — multipart/form-data:
 * `File` (PDF) и `LabelSize`. Гейтвей находит рабочее место, привязанное к пользователю,
 * и пушит задание в SignalR-группу этого рабочего места; конкретный принтер выбирает
 * десктоп-приложение по типу этикетки. Ответ — `{ jobId }` для корреляции с результатом.
 *
 * Отказы (HTTP-ошибка с кодом в теле): нет привязанного рабочего места
 * (`NoWorkstationBinding`), не PDF (`InvalidPdfFile`), больше 3 МБ (`PdfTooLarge`).
 * Вызывающая сторона на ЛЮБУЮ ошибку откатывается на локальную печать — см. printRouter.
 */
export async function createCloudPrintJob(
  pdf: Blob,
  labelSize?: string,
  ctx?: FetchCtx,
): Promise<CreatePrintJobResult> {
  requireCloudPrinterUrl();
  const form = new FormData();
  // Бэк проверяет ContentType файла строго на application/pdf — тип задаём явно,
  // не полагаясь на то, что пришло от сервера этикеток.
  form.append('File', new Blob([pdf], { type: 'application/pdf' }), 'label.pdf');
  if (labelSize) form.append('LabelSize', labelSize);

  const res = await cloudPrinterApi.post<CreatePrintJobResult>('v1/cloud-printer/print-jobs', form, {
    signal: ctx?.signal,
  });
  return res.data;
}
